import React from "react";
import Grid from '@mui/material/Grid';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import {
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Code as SoftwareIcon,
  Storage as StorageIcon,
  Phone as PhoneIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Home() {
  const { t } = useTranslation();
  const homeT = (key: string) => t(`home:${key}`);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const services = [
    {
      icon: <ComputerIcon sx={{ fontSize: 40, color: "#3b82f6" }} />,
      title: homeT("itHelpDeskTitle"),
      link: "/itservices",
      active: true,
      description: homeT("itHelpDeskDescription"),
    },
    {
      icon: <SoftwareIcon sx={{ fontSize: 40, color: "#3b82f6" }} />,
      title: homeT("softwareTitle"),
      link: "/swservices",
      active: true,
      description: homeT("softwareDescription"),
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40, color: "#3b82f6" }} />,
      title: homeT("dataTitle"),
      link: "/dataservices",
      active: false,
      description: homeT("dataDescription"),
    }
  ];

  const features = [
    homeT("features.0"),
    homeT("features.1"),
    homeT("features.2"),
    homeT("features.3"),
    homeT("features.4"),
  ];

  return (
    <Box sx={{ overflow: "hidden", background: "transparent", pl: 0 }}>
      {/* Hero Section */}
      <Box sx={{ flex: 1, m: 0, p: 0, px: 0, py: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Box sx={{ flex: 1, m: 0, p: { xs: 3, md: 4 }, backdropFilter: "blur(10px)", background: "linear-gradient(90deg, transparent 0%, cadetblue 100%)", height: isMobile ? "auto" : "500px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <Typography variant="h2" sx={{ fontWeight: "bold", mb: isMobile ? 2 : 3, textAlign: "center" }}>
            {homeT('heroTitle')}
            <Box component="span" sx={{ background: "var(--header-bg)", backgroundClip: "text", WebkitBackgroundClip: "text", color: "transparent", display: "block", fontWeight: "bold" }}>
              {homeT('heroSubtitle')}
            </Box>
          </Typography>
          <Typography variant="h6" sx={{ mb: isMobile ? 2 : 4, textAlign: "justify", justifyContent: "center", opacity: 0.7, lineHeight: 1.6 }}>
            {homeT('heroDescription')}
          </Typography>
          <Box sx={{ display: "inline-flex", gap: 2, justifyContent: "center", flexWrap: "wrap", flexDirection: "row", alignItems: "center" }}>
            <Button variant="contained" component={Link} to="/contact" sx={{ px: 2, py: 1, transition: "all 0.3s ease" }}>
              {homeT('heroCtaSupport')}
            </Button>
            <Button variant="contained" component="a" href="#our-services-section" sx={{ px: 2, py: 1, transition: "all 0.3s ease" }}>
              {homeT('heroCtaExplore')}
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1, alignItems: "center", justifyContent: "center", display: { xs: "none", md: "flex" } }}>
          <img src="/logos/home.jpg" alt="IT Support Illustration" style={{ width: "100%", minHeight: "500px", maxHeight: "500px", objectFit: "cover" }} />
        </Box>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 5 }} id="our-services-section">
        <Typography variant="h3" textAlign="center" fontWeight="bold" sx={{ mb: isMobile ? 1 : 2 }}>
          {homeT('servicesTitle')}
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ mb: isMobile ? 2 : 6, maxWidth: "600px", mx: "auto", textAlign: "justify" }}>
          {homeT('servicesDescription')}
        </Typography>
        <Grid container spacing={isMobile ? 1 : 3}>
          {services.map((service, index) => (
            <Grid key={index} size={{ sm: 6, md: 4 }}>
              <Link to={service.link ?? "/"} style={{ textDecoration: "none" }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 2, textAlign: "center", height: "100%" }}>
                    <Box sx={{ mb: isMobile ? 0 : 2 }}>{service.icon}</Box>
                    <Typography variant="h5" pb={1}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign={"justify"} pb={2} pl={2} lineHeight={1.8}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ background: `linear-gradient(90deg, #f8fafc 0%, transparent 25%), linear-gradient(270deg, #f8fafc 0%, transparent 25%), linear-gradient(180deg, cadetblue 0%, transparent 80%)`, py: isMobile ? 1 : 4, borderRadius: 12 }}>
        <Container maxWidth="lg">
          <Grid spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ mb: isMobile ? 2 : 4, textAlign: "center" }}>
                {homeT('whyChooseUsTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: isMobile ? 0 : 4, fontSize: { xs: "0.9rem", md: "1.1rem", lg: "1.2rem" } }}>
                {homeT('whyChooseUsDescription')}
              </Typography>
              <List dense>
                {features.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} primaryTypographyProps={{ variant: "h6" }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h1" fontWeight="bold" gutterBottom sx={{ fontWeight: "bold", mb: isMobile ? 2 : 4, textAlign: "center", fontSize: { xs: "1.2rem", md: "2.0rem", lg: "2.5rem" } }}>
          {homeT('ctaTitle')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: "600px", mx: "auto", fontSize: { xs: "0.8rem", lg: "1.2rem" } }}>
          {homeT('ctaDescription')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
          <Button variant="contained" size="large" component={Link} to="/contact" startIcon={<PhoneIcon />} sx={{ px: 4, py: 1.5, background: "linear-gradient(135deg, #D3D3D3 0%, #1e40af 100%)", fontSize: isMobile ? "0.9rem" : "1.1rem", minWidth: isMobile ? "200px" : "auto" }}>
            {homeT('ctaContact')}
          </Button>
          <Button variant="outlined" size="large" component="a" href="#our-services-section" startIcon={<BuildIcon />} sx={{ px: 4, py: 1.5, fontSize: isMobile ? "0.9rem" : "1.1rem", minWidth: isMobile ? "200px" : "auto" }}>
            {homeT('ctaViewServices')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}